import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import { useData } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import TaskDetails from './TaskDetails';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiUser, FiAlertCircle, FiClock, FiCheckCircle, FiEye } = FiIcons;

const TaskList = ({ tasks, onEditTask }) => {
  const { teamMembers, updateTask } = useData();
  const { user, hasPermission } = useAuth();
  const [selectedTask, setSelectedTask] = useState(null);

  const getAssigneeName = (assignedTo) => {
    const member = teamMembers.find(m => m.id === assignedTo);
    return member ? member.name : 'Unassigned';
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'in_progress':
        return 'text-blue-600';
      case 'pending':
        return 'text-yellow-600';
      default:
        return 'text-gray-600';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return FiCheckCircle;
      case 'in_progress':
        return FiClock;
      case 'pending':
        return FiAlertCircle;
      default:
        return FiClock;
    }
  };

  const handleStatusChange = (taskId, newStatus) => {
    updateTask(taskId, { status: newStatus });
  };

  const canUpdateTask = (task) => {
    return hasPermission('all') || 
           hasPermission('assign_tasks') || 
           (hasPermission('update_tasks') && task.assignedTo === user.id);
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-900">
                  Task
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-900">
                  Assignee
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-900">
                  Priority
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-900">
                  Status
                </th>
                <th className="text-left px-6 py-4 text-sm font-medium text-gray-900">
                  Deadline
                </th>
                <th className="text-right px-6 py-4 text-sm font-medium text-gray-900">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              <AnimatePresence>
                {tasks.map((task, index) => (
                  <motion.tr
                    key={task.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ delay: index * 0.05 }}
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-1">
                          {task.title}
                        </h4>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {task.description}
                        </p>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <SafeIcon icon={FiUser} className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-900">
                          {getAssigneeName(task.assignedTo)}
                        </span>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium capitalize ${getPriorityColor(task.priority)}`}>
                        {task.priority}
                      </span>
                    </td>
                    
                    <td className="px-6 py-4">
                      {canUpdateTask(task) ? (
                        <select
                          value={task.status}
                          onChange={(e) => handleStatusChange(task.id, e.target.value)}
                          className="text-sm border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                          <option value="pending">Pending</option>
                          <option value="in_progress">In Progress</option>
                          <option value="completed">Completed</option>
                        </select>
                      ) : (
                        <div className={`flex items-center space-x-1 ${getStatusColor(task.status)}`}>
                          <SafeIcon icon={getStatusIcon(task.status)} className="w-4 h-4" />
                          <span className="text-sm capitalize">
                            {task.status.replace('_', ' ')}
                          </span>
                        </div>
                      )}
                    </td>
                    
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-1 text-gray-600">
                        <SafeIcon icon={FiCalendar} className="w-4 h-4" />
                        <span className="text-sm">
                          {formatDistanceToNow(new Date(task.deadline), { addSuffix: true })}
                        </span>
                      </div>
                    </td>
                    
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => setSelectedTask(task)}
                          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                        >
                          <SafeIcon icon={FiEye} className="w-4 h-4" />
                        </motion.button>
                        
                        {canUpdateTask(task) && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => onEditTask(task)}
                            className="px-3 py-1 text-sm text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          >
                            Edit
                          </motion.button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>

        {tasks.length === 0 && (
          <div className="text-center py-12">
            <SafeIcon icon={FiCheckCircle} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No tasks found</h3>
            <p className="text-gray-600">
              Create your first task or adjust the filters to see results.
            </p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedTask && (
          <TaskDetails
            task={selectedTask}
            onClose={() => setSelectedTask(null)}
          />
        )}
      </AnimatePresence>
    </>
  );
};

export default TaskList;